import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Divider,
  Tag,
  Alert,
  Spin,
  Modal,
  Input,
  message,

  Collapse,
} from 'antd';
import {
  FilePdfOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  PrinterOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import './PublicQuoteView.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface PublicQuote {
  quote_number: string;
  status: string;
  valid_until: string | null;
  is_expired: boolean;
  created_at: string;
  client: {
    name: string;
    address: string;
    city: string;
    postal_code: string;
    country: string;
    vat_number: string;
  };
  contact: {
    name: string;
    email: string;
  };
  items: Array<{
    position: number;
    name: string;
    description: string;
    sku: string;
    quantity: number;
    unit_price: number;
    discount_type: string;
    discount_value: number;
    discount_amount: number;
    line_total: number;
  }>;
  option_groups: Array<{
    name: string;
    description: string;
    items: Array<{
      name: string;
      description: string;
      quantity: number;
      unit_price: number;
      line_total: number;
    }>;
  }>;
  terms: Array<{
    category: string;
    title: string;
    content: string;
  }>;
  subtotal: number;
  discount_type: string;
  discount_value: number;
  discount_amount: number;
  total: number;
  currency: string;
  notes: string;
  payment_terms: string;
  delivery_terms: string;
  tenant?: {
    name: string;
    logo_url: string | null;
  };
}

const PublicQuoteView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acceptModalVisible, setAcceptModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ name: '', email: '', notes: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [viewStartTime] = useState(Date.now());

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const fetchQuote = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/public/quote/${token}`);
      if (!response.ok) {
        throw new Error('Preventivo non trovato');
      }
      const data = await response.json();
      setQuote(data);

      // Log view
      await fetch(`${API_URL}/tracking/view/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          page_url: window.location.href,
          referrer: document.referrer,
        }),
      });
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, sessionId]);

  useEffect(() => {
    fetchQuote();

    // Update view duration periodically
    const interval = setInterval(async () => {
      const duration = Math.floor((Date.now() - viewStartTime) / 1000);
      await fetch(`${API_URL}/tracking/view/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          duration_seconds: duration,
        }),
      });
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [fetchQuote, API_URL, token, sessionId, viewStartTime]);

  const handleDownloadPdf = async () => {
    // Track download
    await fetch(`${API_URL}/tracking/action/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'download_pdf',
        session_id: sessionId,
      }),
    });

    window.open(`${API_URL}/public/quote/${token}/pdf`, '_blank');
  };

  const handleAccept = async () => {
    setAcceptLoading(true);
    try {
      // Track accept click
      await fetch(`${API_URL}/tracking/action/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'click_accept',
          session_id: sessionId,
        }),
      });

      const response = await fetch(`${API_URL}/public/quote/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accepted_by_name: acceptForm.name,
          accepted_by_email: acceptForm.email,
          notes: acceptForm.notes,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore');
      }

      message.success('Preventivo accettato con successo!');
      setAcceptModalVisible(false);
      fetchQuote(); // Refresh status
    } catch (err: any) {
      message.error(err.message || 'Errore nell\'accettazione');
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleReject = async () => {
    setRejectLoading(true);
    try {
      const response = await fetch(`${API_URL}/public/quote/${token}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: rejectReason,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore');
      }

      message.success('Risposta registrata');
      setRejectModalVisible(false);
      fetchQuote();
    } catch (err: any) {
      message.error(err.message || 'Errore');
    } finally {
      setRejectLoading(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: quote?.currency || 'CHF',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="public-quote-loading">
        <Spin size="large" />
        <p>Caricamento preventivo...</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="public-quote-error" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '48px 24px', textAlign: 'center' }}>
        <WarningOutlined style={{ fontSize: 64, color: '#faad14', marginBottom: 24 }} />
        <h2 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 8px 0', color: '#010D69' }}>Preventivo non trovato</h2>
        <p style={{ fontSize: 16, color: '#8c8c8c', margin: 0 }}>Il link potrebbe essere scaduto o non valido.</p>
      </div>
    );
  }

  const columns = [
    {
      title: '#',
      dataIndex: 'position',
      key: 'position',
      width: 50,
    },
    {
      title: 'Descrizione',
      key: 'description',
      render: (record: any) => (
        <div>
          <Text strong>{record.name}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
          {record.sku && (
            <Tag color="default" style={{ marginTop: 4 }}>
              {record.sku}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Qta',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Prezzo',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Totale',
      dataIndex: 'line_total',
      key: 'line_total',
      width: 120,
      align: 'right' as const,
      render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
    },
  ];

  const isAcceptable = quote.status === 'draft' || quote.status === 'sent';
  const isAccepted = quote.status === 'accepted';
  const isRejected = quote.status === 'rejected';

  return (
    <div className="public-quote">
      <div className="public-quote__container">
        {/* Header */}
        <div className="public-quote__header">
          <div className="public-quote__logo">
            {quote.tenant?.logo_url ? (
              <img
                src={quote.tenant.logo_url.startsWith('http') ? quote.tenant.logo_url : `${process.env.REACT_APP_API_URL?.replace('/api', '')}${quote.tenant.logo_url}`}
                alt={quote.tenant?.name || 'Logo'}
                style={{ maxWidth: 180, maxHeight: 60, height: 'auto', objectFit: 'contain' }}
              />
            ) : (
              <Title level={2} style={{ color: '#010D69', margin: 0 }}>
                {quote.tenant?.name || 'QuotaPro'}
              </Title>
            )}
          </div>
          <div className="public-quote__actions">
            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
              Stampa
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadPdf}>
              Scarica PDF
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {quote.is_expired && (
          <Alert
            message="Preventivo scaduto"
            description={`Questo preventivo era valido fino al ${formatDate(quote.valid_until)}. Contattaci per un aggiornamento.`}
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {isAccepted && (
          <Alert
            message="Preventivo accettato"
            description="Grazie! Questo preventivo e stato accettato. Ti contatteremo a breve."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {isRejected && (
          <Alert
            message="Preventivo rifiutato"
            description="Questo preventivo e stato rifiutato. Contattaci per discutere alternative."
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Quote Info */}
        <Card className="public-quote__info">
          <div className="public-quote__info-grid">
            <div className="public-quote__info-section">
              <Text type="secondary">PREVENTIVO</Text>
              <Title level={3} style={{ margin: '8px 0' }}>
                {quote.quote_number}
              </Title>
              <Space direction="vertical" size={4}>
                <Text>Data: {formatDate(quote.created_at)}</Text>
                {quote.valid_until && (
                  <Text>
                    Valido fino: {formatDate(quote.valid_until)}
                    {quote.is_expired && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        Scaduto
                      </Tag>
                    )}
                  </Text>
                )}
              </Space>
            </div>
            <div className="public-quote__info-section">
              <Text type="secondary">CLIENTE</Text>
              <Title level={4} style={{ margin: '8px 0' }}>
                {quote.client.name}
              </Title>
              <Space direction="vertical" size={4}>
                {quote.client.address && <Text>{quote.client.address}</Text>}
                {quote.client.city && (
                  <Text>
                    {quote.client.postal_code} {quote.client.city}
                  </Text>
                )}
                {quote.client.vat_number && (
                  <Text type="secondary">P.IVA: {quote.client.vat_number}</Text>
                )}
              </Space>
            </div>
            <div className="public-quote__info-section">
              <Text type="secondary">CONTATTO</Text>
              <div style={{ marginTop: 8 }}>
                <Text strong>{quote.contact.name}</Text>
                <br />
                <Text>{quote.contact.email}</Text>
              </div>
            </div>
          </div>
        </Card>

        {/* Items Table */}
        <Card title="Dettaglio preventivo" className="public-quote__items">
          <Table
            dataSource={quote.items}
            columns={columns}
            rowKey="position"
            pagination={false}
            size="middle"
          />

          {/* Totals */}
          <div className="public-quote__totals">
            <div className="public-quote__total-row">
              <Text>Subtotale</Text>
              <Text>{formatCurrency(quote.subtotal)}</Text>
            </div>
            {quote.discount_amount > 0 && (
              <div className="public-quote__total-row">
                <Text>
                  Sconto{' '}
                  {quote.discount_type === 'percentage'
                    ? `(${quote.discount_value}%)`
                    : ''}
                </Text>
                <Text type="danger">-{formatCurrency(quote.discount_amount)}</Text>
              </div>
            )}
            <Divider style={{ margin: '12px 0' }} />
            <div className="public-quote__total-row public-quote__total-row--final">
              <Text strong style={{ fontSize: 18 }}>
                TOTALE
              </Text>
              <Text strong style={{ fontSize: 24, color: '#010D69' }}>
                {formatCurrency(quote.total)}
              </Text>
            </div>
          </div>
        </Card>

        {/* Option Groups */}
        {quote.option_groups.length > 0 && (
          <Card title="Opzioni aggiuntive" className="public-quote__options">
            <Collapse>
              {quote.option_groups.map((group, idx) => (
                <Panel header={group.name} key={idx}>
                  {group.description && (
                    <Paragraph type="secondary">{group.description}</Paragraph>
                  )}
                  <Table
                    dataSource={group.items}
                    columns={[
                      { title: 'Descrizione', dataIndex: 'name', key: 'name' },
                      {
                        title: 'Qta',
                        dataIndex: 'quantity',
                        key: 'quantity',
                        width: 80,
                        align: 'center' as const,
                      },
                      {
                        title: 'Prezzo',
                        dataIndex: 'unit_price',
                        key: 'unit_price',
                        width: 120,
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(v),
                      },
                      {
                        title: 'Totale',
                        dataIndex: 'line_total',
                        key: 'line_total',
                        width: 120,
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(v),
                      },
                    ]}
                    rowKey={(_, i) => String(i)}
                    pagination={false}
                    size="small"
                  />
                </Panel>
              ))}
            </Collapse>
          </Card>
        )}

        {/* Terms & Conditions */}
        {quote.terms.length > 0 && (
          <Card title="Termini e condizioni" className="public-quote__terms">
            <Collapse>
              {quote.terms.map((term, idx) => (
                <Panel
                  header={
                    <Space>
                      <Tag>{term.category}</Tag>
                      {term.title}
                    </Space>
                  }
                  key={idx}
                >
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(term.content) }} />
                </Panel>
              ))}
            </Collapse>
          </Card>
        )}

        {/* Notes */}
        {(quote.notes || quote.payment_terms || quote.delivery_terms) && (
          <Card title="Note" className="public-quote__notes">
            {quote.notes && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Note:</Text>
                <Paragraph>{quote.notes}</Paragraph>
              </div>
            )}
            {quote.payment_terms && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Termini di pagamento:</Text>
                <Paragraph>{quote.payment_terms}</Paragraph>
              </div>
            )}
            {quote.delivery_terms && (
              <div>
                <Text type="secondary">Termini di consegna:</Text>
                <Paragraph>{quote.delivery_terms}</Paragraph>
              </div>
            )}
          </Card>
        )}

        {/* Action buttons */}
        {isAcceptable && !quote.is_expired && (
          <div className="public-quote__cta">
            <Space size="large">
              <Button
                size="large"
                onClick={() => setRejectModalVisible(true)}
                icon={<CloseCircleOutlined />}
              >
                Rifiuta
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => setAcceptModalVisible(true)}
                icon={<CheckCircleOutlined />}
                className="public-quote__accept-btn"
              >
                Accetta Preventivo
              </Button>
            </Space>
          </div>
        )}

        {/* Footer */}
        <div className="public-quote__footer">
          <Text type="secondary">
            {quote.tenant?.name || 'QuotaPro'} | Generato con QuotaPro
          </Text>
        </div>
      </div>

      {/* Accept Modal */}
      <Modal
        title="Accetta Preventivo"
        open={acceptModalVisible}
        onCancel={() => setAcceptModalVisible(false)}
        onOk={handleAccept}
        confirmLoading={acceptLoading}
        okText="Conferma Accettazione"
        okButtonProps={{
          style: { backgroundColor: '#10b981', borderColor: '#10b981' },
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text>Nome e Cognome</Text>
            <Input
              placeholder="Il tuo nome completo"
              value={acceptForm.name}
              onChange={(e) =>
                setAcceptForm({ ...acceptForm, name: e.target.value })
              }
            />
          </div>
          <div>
            <Text>Email</Text>
            <Input
              placeholder="La tua email"
              value={acceptForm.email}
              onChange={(e) =>
                setAcceptForm({ ...acceptForm, email: e.target.value })
              }
            />
          </div>
          <div>
            <Text>Note (opzionale)</Text>
            <TextArea
              rows={3}
              placeholder="Eventuali note o richieste..."
              value={acceptForm.notes}
              onChange={(e) =>
                setAcceptForm({ ...acceptForm, notes: e.target.value })
              }
            />
          </div>
        </Space>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Rifiuta Preventivo"
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleReject}
        confirmLoading={rejectLoading}
        okText="Conferma Rifiuto"
        okButtonProps={{ danger: true }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Motivo del rifiuto (opzionale)</Text>
          <TextArea
            rows={4}
            placeholder="Aiutaci a capire perche hai rifiutato..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default PublicQuoteView;
